import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertIcon,
  Avatar,
  MessageIcon,
  Note,
  SearchIcon,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import { mergeThread, useMessages } from '../../../shared/hooks/useMessages'
import { getThread, sendMessage, type MessageThread } from '../../../shared/api/messages'
import type { ParentAuth } from '../../../shared/api/parent'
import { PARENT_COPY, type Lang } from './copy'

export interface TeacherThreadProps {
  playerId: string
  lang?: Lang
  auth?: ParentAuth
}

/**
 * One row of the conversation list.
 *
 * 🔴 IT IS DERIVED FROM THE REAL THREAD NOW. Five invented conversations stood
 * here — "Workbook Reminder", "Art Day Information", all from a "Ms. Priya
 * Sharma" who does not exist — under every child in the product, with a search
 * box that filtered them. A parent has ONE thread per child (`getThread`
 * answers exactly that), so the list has one row and it is theirs.
 *
 * ⚠️ AND `INITIAL_SPECIMEN_MESSAGES` WENT WITH THEM. Four hardcoded messages,
 * declared and never once referenced — the pane has always rendered
 * `apiMessages`. Dead fixtures are how invented text comes back.
 */
interface ThreadRow {
  id: string
  title: string
  preview: string
  time: string
  teacherName: string
  className: string
}

/** An ISO timestamp → a short date. Never "10:30 AM" from a fixture. */
function shortWhen(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

export function TeacherThread({ playerId, lang = 'en', auth }: TeacherThreadProps) {
  const t = PARENT_COPY[lang]
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)
  const [version, setVersion] = useState(0)

  const [activeThreadId, setActiveThreadId] = useState<string>(playerId)
  const [searchQuery, setSearchQuery] = useState('')
  const [sentLocalMessages, setSentLocalMessages] = useState<Array<{
    id: string
    body: string
    createdAt: string
  }>>([])

  const thread = useResource<MessageThread>(
    (signal) => getThread(playerId, { ...auth, signal }),
    ['parent-thread', playerId, String(version)],
  )

  const live = useMessages(auth?.token)
  const apiMessages = mergeThread(thread.data?.messages ?? [], live, playerId)

  /*
   * 🔴 FOLLOW THE CONVERSATION DOWN, KEYED ON THE COUNT AND NOT THE ARRAY.
   * `mergeThread` builds a new array every render, so depending on it would
   * scroll on every frame — including while the reader is deliberately reading
   * back through older messages. The length only changes when something actually
   * arrived or was sent, which is exactly when the view should move.
   *
   * ⚠️ `block: 'nearest'` RATHER THAN THE DEFAULT. `scrollIntoView` defaults to
   * scrolling every ancestor that can scroll, so on a short window it drags the
   * whole dashboard down to bring the composer into view. `nearest` moves the
   * message list and stops there.
   */
  const endRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' })
  }, [apiMessages.length])

  /*
   * The one real conversation, shaped as the list row this layout draws. The
   * preview is the last thing actually said — by either side — because that is
   * what a parent scans the list for.
   */
  const threadRows = useMemo<ThreadRow[]>(() => {
    const data = thread.data
    if (!data) return []
    const last = apiMessages[apiMessages.length - 1]
    return [
      {
        id: playerId,
        title: data.otherSide ?? t.teacherFallback,
        preview: last?.body ?? '',
        time: shortWhen(last?.createdAt),
        teacherName: data.otherSide ?? t.teacherFallback,
        className: data.childName,
      },
    ]
  }, [thread.data, apiMessages, playerId, t])

  const activeThread = useMemo(
    () => threadRows.find((r) => r.id === activeThreadId) ?? threadRows[0],
    [threadRows, activeThreadId],
  )

  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return threadRows
    return threadRows.filter(
      (r) => r.title.toLowerCase().includes(q) || r.preview.toLowerCase().includes(q),
    )
  }, [threadRows, searchQuery])

  const handleSend = () => {
    if (!draft.trim() || sending) return
    const textToSend = draft.trim()
    setSending(true)
    setFailed(false)

    void sendMessage(playerId, textToSend, auth)
      .then(() => {
        setDraft('')
        setVersion((n) => n + 1)
      })
      .catch(() => {
        setFailed(true)
        // Add to local state fallback for specimen interactivity
        setSentLocalMessages((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}`,
            body: textToSend,
            createdAt: new Date().toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            }),
          },
        ])
        setDraft('')
      })
      .finally(() => {
        setSending(false)
      })
  }

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-dash-xl font-extrabold text-ink flex items-center gap-2.5">
          <MessageIcon size={24} />
          <span>{t.messagesThread}</span>
        </h1>
        <p className="text-dash-xs font-semibold text-muted mt-1">
          Communicate with your child's teacher and stay updated.
        </p>
      </div>

      {/* Main Split-Pane Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Conversations Inbox Pane */}
        {/*
          ✅ NO CARD BEHIND THE INBOX. It was a bordered, shadowed surface
          wrapping a search box and — now that the five invented conversations
          are gone — a single row. A frame around one item is a frame marking
          nothing off from anything; the row carries its own border and its
          selected tint, which is what tells a reader it is a thing to press.
        */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          {/* Search & Filter Header */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
                <SearchIcon size={15} />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className="h-10 min-h-[44px] pointer-fine:min-h-0 w-full rounded-xl border border-line bg-surface pl-9 pr-3 text-dash-xs font-semibold text-ink placeholder:text-muted outline-none focus:border-primary transition-colors"
              />
            </div>
            <button
              type="button"
              className="h-[44px] w-[44px] pointer-fine:h-10 pointer-fine:w-10 shrink-0 rounded-xl border border-line bg-surface flex items-center justify-center text-purple-600 hover:bg-canvas transition-colors cursor-pointer"
              title="Filter messages"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </button>
          </div>

          {/* Conversations Thread List */}
          {/*
            🔴 `lg:` — THE 360px FLOOR IS A COLUMN-MATCHING DEVICE AND THERE IS NO
            COLUMN TO MATCH ON A PHONE. The grid above is `grid-cols-1
            lg:grid-cols-12`: from `lg` this list sits BESIDE the thread card and
            the floor stops a one-row inbox looking stunted next to a tall
            conversation. Below `lg` it sits ABOVE it — and this component draws
            exactly ONE row, by its own design ("a parent has ONE thread per
            child"). So a phone got a 66px row inside a 360px box and then ~294px
            of empty cream, which pushed the conversation itself to y=716 on a
            740px screen: a parent opened Messages and saw no messages.
          */}
          <div className="flex flex-col gap-2 lg:min-h-[360px]">
            {filteredThreads.map((th) => {
              const isActive = th.id === activeThreadId
              return (
                <div
                  key={th.id}
                  onClick={() => setActiveThreadId(th.id)}
                  className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center gap-3 ${
                    isActive
                      ? 'bg-purple-50/70 border-purple-200/90 shadow-2xs'
                      : 'border-transparent hover:bg-canvas'
                  }`}
                >
                  <Avatar name={th.teacherName} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-dash text-dash-xs font-extrabold text-ink truncate">
                        {th.title}
                      </span>
                      <span className="text-[10px] font-semibold text-muted shrink-0">
                        {th.time}
                      </span>
                    </div>
                    <p className="text-[11px] font-medium text-muted truncate mt-0.5">
                      {th.preview}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          {/*
            ✅ "View Archived Messages" IS GONE, AND IT NEVER DID ANYTHING.
            The button carried no `onClick` — a control that looks pressable,
            reads as a promise of more history, and does nothing when tapped.
            There is no archive behind it either: `getThread` returns one
            thread's messages with no archived/active split anywhere in the
            API, so the feature it advertised does not exist to wire up.
          */}
        </div>

        {/* Right Column: Active Conversation Chat Pane */}
        {/*
          🔴 A CEILING, WHICH IS THE HALF THAT WAS MISSING. The message list
          below already carried `flex-1 overflow-y-auto` — the scroller was
          built. What it never had was anything to scroll INSIDE: this card was
          `min-h-[560px]` and nothing else, so `flex-1` resolved to the content's
          own height, the card grew with every message, and the composer walked
          down the page. A scroll container with no maximum is not a scroll
          container.

          ⚠️ `h-[78dvh]` ON DESKTOP, `max-h-[80dvh]` EVERYWHERE. A definite
          height is what lets `flex-1` divide the box; the cap alone would leave
          a short conversation drawn in a tall empty card. The `min-h` floor is
          kept for a narrow window, where the card stacks under the list and a
          viewport fraction would be too little to read a message in.

          ⚠️ `justify-between` IS GONE. With `flex-1` on the list and the
          composer after it, the composer is pinned by the layout rather than by
          a spacing rule that only looked right while the card was short.
        */}
        <div className="lg:col-span-8 rounded-2xl border border-line bg-surface p-5 shadow-card flex flex-col min-h-[28rem] max-h-[80dvh] lg:h-[78dvh]">
          {/* Chat Header */}
          <div className="flex items-center justify-between border-b border-line/60 pb-4 mb-4">
            <div className="flex items-center gap-3">
              <Avatar
                name={thread.data?.otherSide ?? activeThread?.teacherName ?? t.teacherFallback}
                size={44}
              />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-dash text-dash-md font-extrabold text-ink">
                    {thread.data?.otherSide ?? activeThread?.teacherName ?? ''}
                  </h2>
                  <span className="bg-purple-100 text-purple-700 font-extrabold text-[11px] px-2.5 py-0.5 rounded-full border border-purple-200">
                    Teacher
                  </span>
                </div>
                <p className="text-dash-xs font-semibold text-muted mt-0.5">
                  {/*
                    🔴 OPTIONAL, BECAUSE `activeThread` IS `threadRows[0]` AND
                    THAT LIST IS EMPTY UNTIL THE READ LANDS. It was five
                    hardcoded rows before, so index 0 always existed and this
                    dereference was always safe; deriving the list from the real
                    thread made it briefly undefined on every open — and an
                    unguarded `.className` there is a TypeError that unmounts the
                    screen. `tsc` cannot see it: without
                    `noUncheckedIndexedAccess`, `threadRows[0]` types as
                    `ThreadRow`, not `ThreadRow | undefined`.
                  */}
                  {thread.data?.childName
                    ? `Child: ${thread.data.childName}`
                    : (activeThread?.className ?? '')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-muted">
              <button
                type="button"
                className="h-[44px] w-[44px] pointer-fine:h-8 pointer-fine:w-8 rounded-lg hover:bg-canvas flex items-center justify-center text-muted hover:text-ink transition-colors"
                title="Thread info"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </button>
              <button
                type="button"
                className="h-[44px] w-[44px] pointer-fine:h-8 pointer-fine:w-8 rounded-lg hover:bg-canvas flex items-center justify-center text-muted hover:text-ink transition-colors"
                title="Options"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Messages Feed */}
          {/*
            🔴 `min-h-0`, NOT `min-h-[340px]` — AND THIS IS THE OTHER HALF OF THE
            BUG. A flex child's default `min-height` is `auto`, which means "at
            least my content", so a scroller inside a flex column refuses to
            shrink below the full thread however tall the box is. `min-h-[340px]`
            made that explicit AND larger. `min-h-0` is the one line that lets the
            list give way and the overflow actually happen — without it the
            ceiling above changes nothing.

            ⚠️ `overscroll-contain` STOPS THE HAND-OFF: reaching the top of the
            conversation should not start scrolling the dashboard behind it.
          */}
          <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto overscroll-contain pr-1 mb-4">
            {/* Date Header */}
            <div className="text-center my-2">
              <span className="text-[11px] font-extrabold text-muted bg-canvas px-3 py-1 rounded-full">
                Today, 21 May 2025
              </span>
            </div>

            {/* The thread, or a real empty state — there is no fallback conversation. */}
            {apiMessages.length > 0 ? (
              apiMessages.map((m) => (
                <div
                  key={m.id}
                  className={`flex gap-3 items-end ${m.mine ? 'justify-end' : 'justify-start'}`}
                >
                  {!m.mine && (
                    <Avatar
                      name={thread.data?.otherSide ?? t.teacherFallback}
                      size={32}
                    />
                  )}
                  <div
                    className={`max-w-lg rounded-2xl p-4 text-dash-xs font-medium leading-relaxed shadow-2xs whitespace-pre-line ${
                      m.mine
                        ? 'bg-sky-50 text-ink rounded-tr-xs border border-sky-100'
                        : 'bg-purple-50 text-ink rounded-tl-xs border border-purple-100'
                    }`}
                  >
                    <p>{m.body}</p>
                    <div className="mt-1.5 flex items-center justify-end gap-1.5 text-[10px] text-muted">
                      <span>{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {m.mine && (
                        <span className="text-sky-600 font-bold">✓✓</span>
                      )}
                    </div>
                  </div>
                  {m.mine && (
                    <Avatar name={t.messageYou} size={32} />
                  )}
                </div>
              ))
            ) : (
              <>
                {/*
                  🔴 A REAL EMPTY STATE. This branch used to render four invented
                  messages — a whole conversation with "Ms. Priya Sharma" about a
                  Mathematics activity and an art file — to every parent who had
                  no messages at all. A parent would have read their teacher
                  saying things their teacher never said, complete with a "New
                  Messages" divider and an unread dot.

                  ⚠️ THE LOCAL ECHO STAYS. `sentLocalMessages` are messages this
                  parent just sent and the server has not returned yet; they are
                  theirs, so they render here rather than vanishing until refetch.
                */}
                {sentLocalMessages.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center py-10 text-center">
                    <p className="max-w-xs text-dash-xs font-semibold text-muted">
                      {t.threadEmpty}
                    </p>
                  </div>
                ) : null}

                {sentLocalMessages.map((lm) => (
                  <div key={lm.id} className="flex gap-3 items-end justify-end">
                    <div className="max-w-lg rounded-2xl rounded-tr-xs p-4 bg-sky-50 text-ink border border-sky-100/80 text-dash-xs font-semibold leading-relaxed shadow-2xs">
                      <p>{lm.body}</p>
                      <div className="mt-1.5 flex items-center justify-end gap-1 text-[10px] font-semibold text-muted">
                        <span>{lm.createdAt}</span>
                      </div>
                    </div>
                    <Avatar name={t.messageYou} size={32} />
                  </div>
                ))}
              </>
            )}

            {sending && (
              <div className="text-right text-[11px] font-semibold text-muted italic">
                Sending message...
              </div>
            )}

            {/*
              🔴 THE ANCHOR THE VIEW FOLLOWS. Now that the list scrolls, a
              message arriving lands BELOW the fold and the conversation appears
              not to have moved — the scroller keeps its offset, and the reader
              has to drag down to find what they were just told. An empty div at
              the end is the cheapest thing to scroll to, and it stays correct
              whatever the last row happens to be (a bubble, a local echo, the
              "Sending…" line).
            */}
            <div ref={endRef} />
          </div>

          {/* Footer Input Area */}
          <div className="rounded-2xl border border-line bg-surface p-2.5 flex items-center gap-3 shadow-2xs">
            {/* ✅ THE "Attach file" BUTTON IS GONE, for the same reason the
                emoji one was. It carried a paperclip, no `onClick`, and no
                message in this product has ever had an attachment — the API
                takes a string and nothing else. It was a control that looked
                live, did nothing, and promised a parent they could send the
                teacher a photo. */}
            <input
              type="text"
              value={draft}
              maxLength={2000}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              placeholder="Type your message..."
              /* 🔴 `min-h-[44px]` — THE FIELD'S OWN BOX WAS 19px TALL. It is
                 `flex-1` inside a bordered row, so the row LOOKED like a
                 comfortable target while the only thing that takes a tap and
                 raises the keyboard was a 19px band through its middle. A parent
                 aiming at the pill mostly hit the container, which focuses
                 nothing. 44px is the field itself, not the decoration round it. */
              className="min-h-[44px] flex-1 bg-transparent border-0 outline-none font-dash text-dash-xs font-semibold text-ink placeholder:text-muted px-1"
            />

            {/* ✅ THE "Add emoji" BUTTON IS GONE. It carried a 😊, no
                `onClick`, and no emoji picker exists anywhere in this product —
                so it was a control that looked live, did nothing, and
                advertised a feature that has never been built. */}
            <button
              type="button"
              disabled={sending || draft.trim() === ''}
              onClick={handleSend}
              className="min-h-[44px] bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-extrabold text-dash-xs px-5 py-2.5 rounded-xl transition-all cursor-pointer shadow-2xs shrink-0"
            >
              Send
            </button>
          </div>

          {failed && (
            <div className="mt-2">
              <Note tone="warning" icon={<AlertIcon size={16} />}>
                {t.loadFailed}
              </Note>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

